import * as React from 'react';
import { ArrowRightIcon } from '@mentimeter/ragnar-visuals';
import { FlexibleText } from '@mentimeter/slides-presentation-ui/text/flexible-text';
import { Box } from '@mentimeter/ragnar-ui/box';
import { MarkdownText } from '@mentimeter/slides-presentation-ui/text/markdown-text';
import type { StepComponentT } from './Steps';

interface LastIndicationT {
  active: boolean;
  animationDuration: number;
}
const LastIndication = React.memo<LastIndicationT>(
  ({ active, animationDuration }: LastIndicationT) => {
    const [opacity, setOpacity] = React.useState(1);

    React.useEffect(() => {
      let timer: ReturnType<typeof setTimeout> | null;
      if (active) {
        timer = setTimeout(() => {
          setOpacity(0);
        }, 1500);
      } else {
        setOpacity(1);
      }
      return () => {
        if (timer) {
          clearTimeout(timer);
        }
      };
    }, [active]);
    return (
      <Box
        width={1}
        mt={2}
        borderStyle="solid"
        borderTopWidth={3}
        borderColor="primary"
        style={{
          opacity,
          transition: `opacity ${animationDuration}ms ease`,
        }}
      />
    );
  },
);

interface BulletT extends StepComponentT {
  isNumbered: boolean;
  number: number;
  letter: string;
  indentations: 1 | 0;
}
export const Bullet = React.memo<BulletT>(
  ({
    label,
    animationDuration,
    isNumbered,
    isActive,
    isLast,
    indentations,
    number,
    letter,
    fontOffset = 0,
  }: BulletT) => {
    const indentationSizeFactor = indentations * 2;

    return (
      <Box
        width="100%"
        data-testid={`bullet-${number}`}
        extend={() => ({
          opacity: isActive ? 1 : 0,
          transition: `opacity ${animationDuration}ms ease`,
        })}
        mt={indentations === 0 ? 2 : 1}
        as="li"
        flexDirection="row"
        alignItems="baseline"
      >
        <Box
          mr={2}
          ml={`${indentationSizeFactor}em`}
          extend={() => ({
            transform: isNumbered ? undefined : 'translateY(0.1em)',
          })}
        >
          <FlexibleText
            color="primary"
            fontSize={10}
            fontOffset={fontOffset - indentationSizeFactor}
          >
            {isNumbered ? (
              `${indentations === 0 ? number : letter}.`
            ) : (
              <ArrowRightIcon size="0.75em" color="primary" />
            )}
          </FlexibleText>
          {isLast && (
            <LastIndication
              animationDuration={animationDuration}
              active={isActive}
            />
          )}
        </Box>
        {/* Every indentation should be smaller in font size */}
        <FlexibleText
          fontOffset={fontOffset - indentationSizeFactor}
          fontSize={10}
          color="text"
        >
          <MarkdownText
            source={label}
            disableLineBreaks={false}
            allowHyperlinks
          />
        </FlexibleText>
      </Box>
    );
  },
);
